import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import Lightbox from "@/components/Lightbox";

const IMAGES = [
  { src: "/assets/living-bar.jpg", alt: { ro: "Livingul comun cu canapea mare, bar și proiecție pe perete", en: "The shared living room with big sofa, bar and wall projection", it: "Il soggiorno comune con grande divano, bar e proiezione a parete" } },
  { src: "/assets/bedroom.jpg", alt: { ro: "Cameră luminoasă cu pat king-size și perete din lemn", en: "Bright room with king-size bed and wooden wall", it: "Camera luminosa con letto king-size e parete in legno" } },
  { src: "/assets/img-006.jpg", alt: { ro: "Colțul de relaxare de la fereastră", en: "The window reading nook", it: "L'angolo relax alla finestra" } },
  { src: "/assets/img-003.jpg", alt: { ro: "Baie proprie cu duș generos și accente de lemn", en: "Private bathroom with generous shower and wood accents", it: "Bagno privato con doccia ampia e accenti in legno" } },
  { src: "/assets/camera-teracota.jpg", alt: { ro: "Camera de la mansardă cu pat mare și banchetă la fereastră", en: "The attic room with big bed and window bench", it: "La camera in mansarda con letto grande e panca alla finestra" } },
  { src: "/assets/img-021.webp", alt: { ro: "Scara interioară din lemn, spre camere", en: "The wooden interior staircase to the rooms", it: "La scala interna in legno, verso le camere" } },
  { src: "/assets/exterior-casa.jpg", alt: { ro: "Casa din exterior, cu grădina și balcoanele cu flori", en: "The house from outside, with the garden and flowered balconies", it: "La casa dall'esterno, con giardino e balconi fioriti" } },
  { src: "/assets/terasa-gratar.jpg", alt: { ro: "Terasa acoperită cu grătarul din cărămidă și locul de stat", en: "The covered terrace with the brick barbecue and the sitting area", it: "La terrazza coperta con il barbecue in mattoni e l'area relax" } },
  { src: "/assets/foc-curte.jpg", alt: { ro: "Focul din curte, seara, cu șezlongurile pregătite", en: "The courtyard fire pit in the evening, deckchairs ready", it: "Il falò in cortile la sera, con le sdraio pronte" } },
  { src: "/assets/balcon-hamac.jpg", alt: { ro: "Balconul cu hamac, cu vedere spre deal și biserica vecină", en: "The hammock balcony overlooking the hill and the neighbouring church", it: "Il balcone con amaca, vista sulla collina e sulla chiesa vicina" } },
  { src: "/assets/img-004.jpg", alt: { ro: "Curtea și bucătăria de vară, seara", en: "The courtyard and summer kitchen in the evening", it: "Il cortile e la cucina estiva, di sera" } },
  { src: "/assets/img-009.webp", alt: { ro: "Casa la apus, cu ferestrele aprinse", en: "The house at sunset, windows glowing", it: "La casa al tramonto, con le finestre illuminate" } },
  { src: "/assets/img-007.jpg", alt: { ro: "Imperfect House văzută de sus, între dealurile Râșnovului", en: "Imperfect House from above, between the hills of Râșnov", it: "Imperfect House dall'alto, tra le colline di Râșnov" } },
  { src: "/assets/img-010.webp", alt: { ro: "Râșnovul și biserica vecine, văzute de sus", en: "Râșnov and the neighbouring church from above", it: "Râșnov e la chiesa vicina dall'alto" } },
  { src: "/assets/img-011.webp", alt: { ro: "Cetatea Râșnov, la 5 minute de casă", en: "Râșnov Fortress, 5 minutes from the house", it: "La Fortezza di Râșnov, a 5 minuti da casa" } },
];

export default function Gallery() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(() => setOpen((o) => (o === null ? null : (o - 1 + IMAGES.length) % IMAGES.length)), []);
  const next = useCallback(() => setOpen((o) => (o === null ? null : (o + 1) % IMAGES.length)), []);

  return (
    <section id="galerie" className="py-24 md:py-32" data-testid="gallery-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("gal.eyebrow")}
        </motion.p>
        <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main max-w-xl">
            {t("gal.title")}
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
            className="text-brown/80 font-light max-w-md">
            {t("gal.sub")}
          </motion.p>
        </div>

        <div className="mt-14 columns-2 md:columns-3 gap-4 [column-fill:balance]" data-testid="gallery-grid">
          {IMAGES.map((img, i) => (
            <motion.button
              key={img.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
              onClick={() => setOpen(i)}
              data-testid={`gallery-item-${i}`}
              className="group relative block w-full mb-4 break-inside-avoid overflow-hidden rounded-2xl focus:outline-none focus:ring-2 focus:ring-terracotta/60"
              aria-label={img.alt[lang] || img.alt.en || img.alt.ro}
            >
              <img
                src={img.src}
                alt={img.alt[lang] || img.alt.en || img.alt.ro}
                loading="lazy"
                className="w-full h-auto group-hover:scale-105 transition-transform duration-700"
              />
              <span className="absolute inset-0 bg-main/0 group-hover:bg-main/15 transition-colors duration-500" />
            </motion.button>
          ))}
        </div>
      </div>

      <Lightbox images={IMAGES} index={open} onClose={close} onPrev={prev} onNext={next} lang={lang} />
    </section>
  );
}
